import { useEffect, useState } from "react";

import {
    ChevronDown,
    ChevronRight,
    FolderOpen,
    Search,
    CirclePlus,
    Stethoscope
} from "lucide-react";
import { procedures } from "../../data/procedures";
import {
    doc,
    getDoc,
    onSnapshot,
    setDoc
} from "../../access/firestore";
import ProcedureCard from "./ProcedureCard";
import { db } from "../../firebase";

import { useClinic } from "../../context/ClinicContext";

export default function ProceduresSettings() {

    const { clinicId } = useClinic();

    const [expandedSpecialty, setExpandedSpecialty] = useState(null);

    const [search, setSearch] = useState("");

    const [localProcedures, setLocalProcedures] = useState([]);

    const [showModal, setShowModal] = useState(false);

    const [editingProcedure, setEditingProcedure] = useState(null);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [procedureToDelete, setProcedureToDelete] = useState(null);

    const [form, setForm] = useState({

        specialty: "",

        category: "",

        name: "",

        requiresOdontogram: false,

        allowsSurfaces: false

    });

    const [newCategory, setNewCategory] = useState("");

    const [creatingCategory, setCreatingCategory] = useState(false);

    useEffect(() => {

        if (!clinicId) return;

        const ref = doc(

            db,

            "clinics",

            clinicId,

            "settings",

            "procedures"

        );

        const init = async () => {

            const snap = await getDoc(ref);

            if (!snap.exists()) {

                await setDoc(ref, {

                    data: JSON.parse(

                        JSON.stringify(procedures)

                    )

                });

            }

        };

        init();

        const unsubscribe = onSnapshot(

            ref,

            (snap) => {

                if (!snap.exists()) return;

                const data = snap.data();

                setLocalProcedures(

                    data.data || []

                );

            }

        );

        return () => unsubscribe();

    }, [clinicId]);

    function openCreateModal(specialty) {

        setEditingProcedure(null);

        setCreatingCategory(false);

        setNewCategory("");

        setForm({

            specialty: specialty.specialty,

            category:

                specialty.categories.length

                    ? specialty.categories[0].name

                    : "",

            name: "",

            requiresOdontogram: false,

            allowsSurfaces: false

        });

        setShowModal(true);

    }

    function openEditModal(

        specialty,

        category,

        procedure

    ) {

        setEditingProcedure(procedure);

        setCreatingCategory(false);

        setNewCategory("");

        setForm({

            specialty: specialty.specialty,

            category: category.name,

            name: procedure.name,

            requiresOdontogram:

                procedure.requiresOdontogram,

            allowsSurfaces:

                procedure.allowsSurfaces

        });

        setShowModal(true);

    }

    function openDeleteDialog(procedure){

        setProcedureToDelete(procedure);

        setShowDeleteDialog(true);

    }

    async function saveProcedure() {

        if (!form.name.trim()) {

            alert("Escribe un nombre para el procedimiento.");

            return;

        }

        const copy = JSON.parse(

            JSON.stringify(localProcedures)

        );

        const specialty = copy.find(

            s => s.specialty === form.specialty

        );

        if (!specialty) return;

        let category = specialty.categories.find(

            c => c.name === form.category

        );

        if (

            creatingCategory &&

            newCategory.trim()

        ) {

            category = {

                name: newCategory.trim(),

                procedures: []

            };

            specialty.categories.push(category);

        }

        if (!category) return;

        if (editingProcedure) {

            const procedure = category.procedures.find(

                p => p.id === editingProcedure.id

            );

            if (!procedure) return;

            procedure.name = form.name;

            procedure.requiresOdontogram =
                form.requiresOdontogram;

            procedure.allowsSurfaces =
                form.allowsSurfaces;

        } else {

            category.procedures.push({

                id: crypto.randomUUID(),

                name: form.name,

                requiresOdontogram:
                    form.requiresOdontogram,

                allowsSurfaces:
                    form.allowsSurfaces

            });

        }

        await setDoc(

            doc(

                db,

                "clinics",

                clinicId,

                "settings",

                "procedures"

            ),

            {

                data: copy

            }

        );

        setShowModal(false);

        setEditingProcedure(null);

        setCreatingCategory(false);

        setNewCategory("");

        setForm({

            specialty: "",

            category: "",

            name: "",

            requiresOdontogram: false,

            allowsSurfaces: false

        });

    }

    async function deleteProcedure() {

        const copy = JSON.parse(

            JSON.stringify(localProcedures)

        );

        copy.forEach(specialty => {

            specialty.categories.forEach(category => {

                category.procedures =

                    category.procedures.filter(

                        p =>

                            p.id !==

                            procedureToDelete.id

                    );

            });

        });

        await setDoc(

            doc(

                db,

                "clinics",

                clinicId,

                "settings",

                "procedures"

            ),

            {

                data: copy

            }

        );

        setShowDeleteDialog(false);

    }

    const totalProcedures = localProcedures.reduce(
        (total, specialty) => total + specialty.categories.reduce(
            (categoryTotal, category) => categoryTotal + category.procedures.length,
            0
        ),
        0
    );

    const totalCategories = localProcedures.reduce(
        (total, specialty) => total + specialty.categories.length,
        0
    );

    return (

        <div
            className="vd-settings-module vd-settings-procedures"
            style={{
                background: "#fff",
                padding: 24,
                borderRadius: 24,
                maxWidth: 1000
            }}
        >

            <div className="vd-settings-module-header">
                <div>
                    <p className="vd-settings-module-kicker">Operación clínica</p>
                    <h2>Catálogo de procedimientos</h2>
                    <p>Define lo que tu equipo puede presupuestar, registrar y cobrar sin buscar en listas externas.</p>
                </div>
                <span className="vd-settings-module-badge"><Stethoscope size={14} /> Catálogo del consultorio</span>
            </div>

            <div className="vd-settings-module-stats">
                <div><strong>{totalProcedures}</strong><span>procedimientos activos</span></div>
                <div><strong>{totalCategories}</strong><span>categorías organizadas</span></div>
                <div><strong>{localProcedures.length}</strong><span>especialidades</span></div>
            </div>

            <div
                className="vd-settings-module-toolbar"
                style={{
                    marginTop: 24,
                    marginBottom: 24,
                    position: "relative"
                }}
            >

                <Search
                    size={18}
                    style={{
                        position: "absolute",
                        left: 14,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#94a3b8"
                    }}
                />

                <input
                    placeholder="Buscar procedimiento..."
                    value={search}
                    onChange={(e)=>setSearch(e.target.value)}
                    style={{
                        width:"100%",
                        padding:"14px 18px 14px 44px",
                        borderRadius:14,
                        border:"1px solid #dbe2ea",
                        outline:"none",
                        fontSize:15
                    }}
                />

            </div>

            <div
                style={{
                    marginTop: 30,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12
                }}
            >

                {

                    localProcedures
                        .filter(specialty =>
                            specialty.categories.some(category =>
                                category.procedures.some(proc =>
                                    proc.name
                                        .toLowerCase()
                                        .includes(search.toLowerCase())
                                )
                            )
                        )
                        .map(specialty => (

                        <div
                            key={specialty.specialty}
                            style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 14,
                                overflow: "hidden"
                            }}
                        >

                            <div

                                onClick={() =>

                                    setExpandedSpecialty(

                                        expandedSpecialty === specialty.specialty
                                            ? null
                                            : specialty.specialty

                                    )

                                }

                                style={{

                                    padding: 18,

                                    cursor: "pointer",

                                    display: "flex",

                                    justifyContent: "space-between",

                                    alignItems: "center",

                                    fontWeight: 700,

                                    background: "#f8fafc"

                                }}

                            >

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12
                                    }}
                                >

                                    <Stethoscope
                                        size={18}
                                        color="#2563eb"
                                    />

                                    <span>

                                        {specialty.specialty}

                                    </span>

                                </div>

                                <span>

                                    {
                                        expandedSpecialty === specialty.specialty
                                            ? <ChevronDown size={18}/>
                                            : <ChevronRight size={18}/>
                                    }

                                </span>

                            </div>

                            {
                                expandedSpecialty === specialty.specialty && (

                                    <div
                                        style={{
                                            padding: 20,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 28
                                        }}
                                    >

                                        {

                                            specialty.categories
                                                .filter(category =>
                                                    category.procedures.some(proc =>
                                                        proc.name
                                                            .toLowerCase()
                                                            .includes(search.toLowerCase())
                                                    )
                                                )
                                                .map(category => (

                                                <div

                                                    key={category.name}

                                                    style={{

                                                        border:"1px solid #e2e8f0",

                                                        borderRadius:16,

                                                        background:"#ffffff",

                                                        padding:18,

                                                        boxShadow:"0 1px 3px rgba(0,0,0,.04)"

                                                    }}

                                                >

                                                    <div

                                                        style={{

                                                            display:"flex",

                                                            justifyContent:"space-between",

                                                            alignItems:"center",

                                                            marginBottom:18,

                                                            paddingBottom:12,

                                                            borderBottom:"1px solid #eef2f7"

                                                        }}

                                                    >

                                                        <div

                                                            style={{

                                                                display:"flex",

                                                                alignItems:"center",

                                                                gap:10,

                                                                fontWeight:700,

                                                                color:"#1e293b"

                                                            }}

                                                        >

                                                            <FolderOpen

                                                                size={18}

                                                                color="#2563eb"

                                                            />

                                                            {category.name}

                                                        </div>

                                                        <span

                                                            style={{

                                                                fontSize:12,

                                                                color:"#64748b",

                                                                background:"#f8fafc",

                                                                padding:"4px 10px",

                                                                borderRadius:999

                                                            }}

                                                        >

                                                            {category.procedures.length} procedimientos

                                                        </span>

                                                    </div>

                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: 8,
                                                            marginLeft: 10
                                                        }}
                                                    >

                                                        {

                                                            category.procedures
                                                                .filter(proc =>
                                                                    proc.name
                                                                        .toLowerCase()
                                                                        .includes(search.toLowerCase())
                                                                )
                                                                .map(proc => (

                                                                <ProcedureCard

                                                                    key={proc.id}

                                                                    procedure={proc}

                                                                    onEdit={() =>

                                                                        openEditModal(

                                                                            specialty,

                                                                            category,

                                                                            proc

                                                                        )

                                                                    }

                                                                    onDelete={() =>

                                                                        openDeleteDialog(

                                                                            proc

                                                                        )

                                                                    }

                                                                />

                                                            ))

                                                        }

                                                    </div>

                                                </div>

                                            ))

                                        }

                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                marginTop: 8
                                            }}
                                        >

                                            <button

                                                onClick={() => openCreateModal(specialty)}

                                                style={{

                                                    display: "flex",

                                                    alignItems: "center",

                                                    gap: 8,

                                                    background: "#2563eb",

                                                    color: "#fff",

                                                    border: "none",

                                                    padding: "10px 18px",

                                                    borderRadius: 12,

                                                    cursor: "pointer",

                                                    fontWeight: 600,

                                                    fontSize: 14

                                                }}

                                            >

                                                <CirclePlus size={18}/>

                                                Agregar procedimiento

                                            </button>

                                        </div>

                                    </div>

                                )
                            }

                        </div>

                    ))

                }

            </div>

            {
                showModal && (

                            <div

                                style={{

                                position:"fixed",

                                inset:0,

                                background:"rgba(15,23,42,.45)",

                                display:"flex",

                                justifyContent:"center",

                                alignItems:"center",

                                zIndex:99999

                                }}

                            >

                            <div

                                style={{

                                width:520,

                                background:"#fff",

                                borderRadius:20,

                                padding:28,

                                boxShadow:"0 20px 60px rgba(0,0,0,.20)"

                                }}

                                >

                                    <h2
                                    style={{
                                    marginTop:0
                                    }}
                                    >

                                    {

                                    editingProcedure

                                    ?

                                    "Editar procedimiento"

                                    :

                                    "Nuevo procedimiento"

                                    }

                                    </h2>

                            <div
                                style={{
                                    marginTop: 24
                                }}
                            >

                                <label
                                    style={{
                                        display: "block",
                                        marginBottom: 8,
                                        fontWeight: 600
                                    }}
                                >

                                    Categoría

                                </label>

                                <select

                                    value={form.category}

                                    onChange={(e)=>

                                        setForm({

                                            ...form,

                                            category: e.target.value

                                        })

                                    }

                                    style={{

                                        width:"100%",

                                        padding:12,

                                        border:"1px solid #dbe2ea",

                                        borderRadius:10,

                                        fontSize:15

                                    }}

                                >

                                    {

                                        localProcedures

                                            .find(

                                                s =>

                                                    s.specialty === form.specialty

                                            )

                                            ?.categories

                                            .map(category => (

                                                <option

                                                    key={category.name}

                                                    value={category.name}

                                                >

                                                    {category.name}

                                                </option>

                                            ))

                                    }

                                </select>

                                <div
                                    style={{
                                        marginTop: 12
                                    }}
                                >

                                    <button

                                        type="button"

                                        onClick={() =>

                                            setCreatingCategory(

                                                !creatingCategory

                                            )

                                        }

                                        style={{

                                            background: "transparent",

                                            border: "none",

                                            color: "#2563eb",

                                            cursor: "pointer",

                                            fontWeight: 600,

                                            padding: 0

                                        }}

                                    >

                                        + Crear nueva categoría

                                    </button>

                                </div>

                                {
                                    creatingCategory && (

                                        <div
                                            style={{
                                                marginTop: 16
                                            }}
                                        >

                                            <input

                                                placeholder="Nombre de la nueva categoría"

                                                value={newCategory}

                                                onChange={(e)=>

                                                    setNewCategory(

                                                        e.target.value

                                                    )

                                                }

                                                style={{

                                                    width: "100%",

                                                    padding: 12,

                                                    border: "1px solid #dbe2ea",

                                                    borderRadius: 10,

                                                    fontSize: 15

                                                }}

                                            />

                                        </div>

                                    )
                                }

                            </div>
                            
                            <div
                                style={{
                                    marginTop:25
                                }}
                            >

                                <label>

                                    Nombre

                                </label>

                                <input

                                    value={form.name}

                                    onChange={(e)=>

                                    setForm({

                                    ...form,

                                    name:e.target.value

                                    })

                                    }

                                    style={{

                                    width:"100%",

                                    marginTop:8,

                                    padding:12,

                                    border:"1px solid #dbe2ea",

                                    borderRadius:10,

                                    fontSize:15

                                    }}

                                />

                            </div>

                            <div
                                style={{
                                display:"flex",
                                gap:25,
                                marginTop:22
                                }}
                            >

                            <label>

                                <input

                                    type="checkbox"

                                    checked={form.requiresOdontogram}

                                    onChange={(e)=>

                                    setForm({

                                    ...form,

                                    requiresOdontogram:e.target.checked

                                    })

                                    }

                                />

                                {" "}Requiere odontograma

                            </label>

                            <label>

                                <input

                                    type="checkbox"

                                    checked={form.allowsSurfaces}

                                    onChange={(e)=>

                                    setForm({

                                    ...form,

                                    allowsSurfaces:e.target.checked

                                    })

                                    }

                                />

                                {" "}Permite superficies

                            </label>

                            </div>

                            <div

                                style={{

                                display:"flex",

                                justifyContent:"flex-end",

                                gap:12,

                                marginTop:30

                                }}

                            >

                            <button

                                onClick={()=>setShowModal(false)}

                                style={{

                                padding:"10px 18px",

                                borderRadius:10,

                                border:"1px solid #dbe2ea",

                                background:"#fff",

                                cursor:"pointer"

                                }}

                            >

                                Cancelar

                            </button>

                            <button

                                onClick={saveProcedure}

                                style={{

                                padding:"10px 18px",

                                borderRadius:10,

                                border:"none",

                                background:"#2563eb",

                                color:"#fff",

                                cursor:"pointer"

                                }}

                                >

                                Guardar

                            </button>

                        </div>

                    </div>

                </div>

                )
            }

            {
                showDeleteDialog && (

                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(15,23,42,.45)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 99999
                        }}
                    >

                        <div
                            style={{
                                width: 420,
                                background: "#fff",
                                borderRadius: 20,
                                padding: 28
                            }}
                        >

                            <h2
                                style={{
                                    marginTop: 0
                                }}
                            >
                                Eliminar procedimiento
                            </h2>

                            <p>

                                ¿Deseas eliminar el procedimiento

                                <strong>

                                    {" "}

                                    {procedureToDelete?.name}

                                </strong>

                                ?

                            </p>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 12,
                                    marginTop: 30
                                }}
                            >

                                <button

                                    onClick={() =>

                                        setShowDeleteDialog(false)

                                    }

                                >

                                    Cancelar

                                </button>

                                <button

                                    onClick={deleteProcedure}

                                    style={{

                                        background: "#dc2626",

                                        color: "#fff",

                                        border: "none",

                                        padding: "10px 18px",

                                        borderRadius: 10,

                                        cursor: "pointer"

                                    }}

                                >

                                    Eliminar

                                </button>

                            </div>

                        </div>

                    </div>

                )
            }

        </div>

    );

}
